import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { ChangeEvent, DragEvent, useRef, useState } from "react";
import Field from "./Field";
import { RESUME_ACCEPT, RESUME_MAX_BYTES } from "./resumeRules";
import { FormErrors, IPersonalDetails } from "./types";

type ResumeProps = {
  onPrev: () => void;
  handlePersonalDetails: (event: ChangeEvent<HTMLInputElement>) => void;
  personalDetails: IPersonalDetails;
  /** The chosen resume; the form owns it, this only displays it. */
  resume: File | null;
  /** Called with the picked file, or null when it is removed. */
  onFileSelected: (file: File | null) => void;
  handleSubmit: () => void;
  errors: FormErrors;
  isSubmitting?: boolean;
};

const fileSize = (size: number): string => {
  if (size < 1024) return `${size} bytes`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const Resume = ({
  onPrev,
  handlePersonalDetails,
  personalDetails,
  resume,
  onFileSelected,
  handleSubmit,
  errors,
  isSubmitting = false,
}: ResumeProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const clearInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const pick = (file: File | null) => {
    onFileSelected(file);
    // Let the same file be picked again after removal or rejection.
    clearInput();
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) pick(file);
  };

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h2 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
        Your resume
      </h2>

      <div className="flex flex-col py-2">
        {resume ? (
          <>
          <label htmlFor="resume" className="sr-only">
            Upload your resume to replace {resume.name}
          </label>
          <div className="flex w-full items-center justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="rounded-full bg-orange-100 p-2">
                <DocumentIcon className="h-5 w-5 text-orange-600" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {resume.name}
                </p>
                <p className="text-xs text-gray-600">{fileSize(resume.size)}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => pick(null)}
              className="rounded-full p-2 text-gray-700 hover:bg-orange-100"
              aria-label={`Remove ${resume.name}`}
            >
              <TrashIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          </>
        ) : (
          <label
            htmlFor="resume"
            className={`flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 text-center transition-colors focus-within:ring-2 focus-within:ring-orange-300 ${
              isDragging ? "border-orange-400 bg-orange-50" : "border-orange-200"
            } hover:bg-orange-50`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
          >
            <span className="rounded-full bg-orange-100 p-3">
              <CloudArrowUpIcon className="w-7 text-orange-600" aria-hidden="true" />
            </span>
            <span className="mt-2">
              <span className="font-semibold text-gray-900">Upload your resume</span>{" "}
              <span className="text-gray-600">or drag and drop it here</span>
            </span>
            <span className="text-sm text-gray-600">
              PDF, DOC or DOCX, up to {RESUME_MAX_BYTES / (1024 * 1024)} MB
            </span>
          </label>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="sr-only"
          name="resume"
          id="resume"
          accept={RESUME_ACCEPT}
          aria-invalid={errors.resume ? true : undefined}
          aria-describedby={errors.resume ? "resume-error" : undefined}
          onChange={(event) => pick(event.target.files?.[0] ?? null)}
        />
        {errors.resume && (
          <p id="resume-error" role="alert" className="mt-1 text-xs text-red-600">
            {errors.resume}
          </p>
        )}
      </div>

      <Field
        id="linkedIn"
        label="LinkedIn"
        type="url"
        placeholder="https://linkedin.com/in/…"
        value={personalDetails.linkedIn}
        onChange={handlePersonalDetails}
        error={errors.linkedIn}
      />
      <Field
        id="portfolio"
        label="Portfolio"
        type="url"
        value={personalDetails.portfolio}
        onChange={handlePersonalDetails}
        error={errors.portfolio}
      />
      <Field
        id="otherLink"
        label="Other link"
        type="url"
        value={personalDetails.otherLink}
        onChange={handlePersonalDetails}
        error={errors.otherLink}
      />

      <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          className="flex items-center justify-center gap-2 rounded-lg border border-orange-200 px-4 py-3 text-orange-700 hover:bg-orange-50"
          onClick={onPrev}
          disabled={isSubmitting}
        >
          <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex items-center justify-center rounded-lg bg-orange-500 px-6 py-3 font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-orange-300"
        >
          {isSubmitting ? "Submitting…" : "Submit application"}
        </button>
      </div>
    </form>
  );
};

export default Resume;
